import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  Search,
  Lock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  ListChecks,
  Loader,
  AlertTriangle,
  Tag,
  ClipboardList,
} from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import balanceComparisonService from '../../../../services/balanceComparisonService';
import { userProfileService } from '../../../../services/userProfileService';
import {
  BalanceComparisonRow,
  BalanceComparisonLocation,
  BalanceComparisonSummary,
  BalanceComparisonFilter,
} from '../../../../types/balanceComparison';
import { UserProfileDto } from '../../../../types/userProfile';
import { MovementInsightBadge } from './MovementInsightBadge';
import { SnapshotInfoBanner } from './SnapshotInfoBanner';
import { CountFromDiffResultKind, CreateCountFromDiffModal, SelectedLocationLine } from './CreateCountFromDiffModal';
import { useStatusOptions } from '../../../common/statusPresentation';

interface BalanceComparisonTabProps {
  warehouseID: string;
}

const PAGE_SIZE = 50;

/** Composite key for one location selection: "{warehouseId}|{itemNo}|{binCode}" */
type LocationKey = string;

const locKey = (warehouseID: string, itemNo: string, binCode: string): LocationKey =>
  `${warehouseID}|${itemNo}|${binCode}`;

/**
 * Deterministisk fargepalett for lokasjonskategorier. Samme StcBinCatNo gir
 * alltid samme farge. Klassene er statiske strenger (Tailwind-analyserbare) og
 * har dark-varianter slik at de fungerer i alle temaer.
 */
const CATEGORY_BADGE_COLORS = [
  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300',
  'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300',
];

const categoryBadgeClass = (catNo?: number | null): string =>
  catNo == null ? '' : CATEGORY_BADGE_COLORS[Math.abs(catNo) % CATEGORY_BADGE_COLORS.length];

/**
 * Tab on the warehouse manager's CountingDashboard showing the
 * WMS-vs-ERP balance comparison view. Rows can be expanded to show locations.
 * Manager can select whole items (= all bins) or individual bins.
 */
export const BalanceComparisonTab: React.FC<BalanceComparisonTabProps> = ({ warehouseID }) => {
  const { t } = useTranslation();

  // Lokasjonskategori-tekst (ST42) fra master-StatusCodes — språkbevisst (Model A).
  // Fargen (categoryBadgeClass) er bevisst bespoke og beholdes.
  const binCategoryStatusOptions = useStatusOptions(42);
  const binCategoryTextMap = useMemo(
    () => new Map(binCategoryStatusOptions.map(o => [o.value, o.text])),
    [binCategoryStatusOptions]
  );

  // Data state
  const [rows, setRows] = useState<BalanceComparisonRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState<BalanceComparisonSummary | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [onlyOutOfSync, setOnlyOutOfSync] = useState(true);
  const [onlyCountable, setOnlyCountable] = useState<boolean | undefined>(undefined);
  const [includeEmptyBins, setIncludeEmptyBins] = useState(true);
  // Skjul varer med WMS-bevegelse etter siste ERP-oppdatering ("Sist oppdatert") — ERP har ikke sett dem ennå
  const [hideMovedSinceSnapshot, setHideMovedSinceSnapshot] = useState(true);
  const [itemSearch, setItemSearch] = useState('');
  const [appliedItemSearch, setAppliedItemSearch] = useState('');

  // Expansion state — set of row ids that are open
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Selection — set of LocationKey (per bin) that are checked.
  const [selectedLocs, setSelectedLocs] = useState<Set<LocationKey>>(new Set());

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [profiles, setProfiles] = useState<UserProfileDto[]>([]);

  // ----------------------------------------------------------------
  // Loading
  // ----------------------------------------------------------------

  const loadData = useCallback(async () => {
    if (!warehouseID) return;
    setLoading(true);
    setError(null);

    try {
      const filter: BalanceComparisonFilter = {
        warehouseID,
        page,
        pageSize: PAGE_SIZE,
        onlyOutOfSync,
        onlyCountable,
        itemNoSearch: appliedItemSearch || undefined,
        sortBy: 'DiffAbs',
        sortDirection: 'desc',
        includeEmptyBins,
        hideMovedSinceSnapshot,
      };

      const [result, summaryData] = await Promise.all([
        balanceComparisonService.getComparison(filter),
        balanceComparisonService.getSummary(warehouseID),
      ]);

      setRows(result.rows);
      setTotalCount(result.totalCount);
      setSummary(summaryData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [warehouseID, page, onlyOutOfSync, onlyCountable, appliedItemSearch, includeEmptyBins, hideMovedSinceSnapshot]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load operator profiles (once) — same source as the existing CountingListOverview module
  useEffect(() => {
    userProfileService
      .getAllProfilesForCompany()
      .then((data) => setProfiles(data))
      .catch((err) => console.error('Could not load operator profiles:', err));
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [onlyOutOfSync, onlyCountable, appliedItemSearch, warehouseID, includeEmptyBins, hideMovedSinceSnapshot]);

  // Clear selection when warehouse or search filter changes (selection makes no sense across contexts)
  useEffect(() => {
    setSelectedLocs(new Set());
    setExpandedRows(new Set());
  }, [warehouseID, appliedItemSearch]);

  // ----------------------------------------------------------------
  // Selection helpers
  // ----------------------------------------------------------------

  /** Bins of an item that are SELECTABLE (not blocked, not on active count list) */
  const selectableLocations = useCallback(
    (row: BalanceComparisonRow): BalanceComparisonLocation[] => {
      if (!row.isCountable) return [];
      return row.locations.filter((l) => !l.isOnActiveCountList);
    },
    []
  );

  /** Tri-state: 'all' | 'some' | 'none' for an item */
  const itemSelectionState = useCallback(
    (row: BalanceComparisonRow): 'all' | 'some' | 'none' => {
      const selectable = selectableLocations(row);
      if (selectable.length === 0) return 'none';
      const selectedCount = selectable.filter((l) =>
        selectedLocs.has(locKey(row.warehouseID, row.itemNo, l.binCode))
      ).length;
      if (selectedCount === 0) return 'none';
      if (selectedCount === selectable.length) return 'all';
      return 'some';
    },
    [selectedLocs, selectableLocations]
  );

  const toggleItem = (row: BalanceComparisonRow) => {
    const selectable = selectableLocations(row);
    if (selectable.length === 0) return;
    const next = new Set(selectedLocs);
    const currentlyAllSelected = itemSelectionState(row) === 'all';
    selectable.forEach((l) => {
      const key = locKey(row.warehouseID, row.itemNo, l.binCode);
      if (currentlyAllSelected) next.delete(key);
      else next.add(key);
    });
    setSelectedLocs(next);
  };

  const toggleLocation = (row: BalanceComparisonRow, loc: BalanceComparisonLocation) => {
    if (loc.isOnActiveCountList || !row.isCountable) return;
    const key = locKey(row.warehouseID, row.itemNo, loc.binCode);
    const next = new Set(selectedLocs);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedLocs(next);
  };

  const toggleExpand = (row: BalanceComparisonRow) => {
    if (row.presence === 'ErpOnly') return; // No locations to show
    const next = new Set(expandedRows);
    if (next.has(row.id)) next.delete(row.id);
    else next.add(row.id);
    setExpandedRows(next);
  };

  // Total selected across all visible rows
  const totalSelectedOnPage = useMemo(() => {
    let count = 0;
    for (const row of rows) {
      for (const loc of row.locations) {
        if (selectedLocs.has(locKey(row.warehouseID, row.itemNo, loc.binCode))) count++;
      }
    }
    return count;
  }, [rows, selectedLocs]);

  // Build the selection passed to the modal
  const selectedLines: SelectedLocationLine[] = useMemo(() => {
    const lines: SelectedLocationLine[] = [];
    for (const row of rows) {
      for (const loc of row.locations) {
        if (selectedLocs.has(locKey(row.warehouseID, row.itemNo, loc.binCode))) {
          lines.push({
            warehouseID: row.warehouseID,
            itemNo: row.itemNo,
            description: row.description,
            binCode: loc.binCode,
            wmsQuantity: loc.wmsQuantity,
          });
        }
      }
    }
    return lines;
  }, [rows, selectedLocs]);

  // ----------------------------------------------------------------
  // Render helpers
  // ----------------------------------------------------------------

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const formatDateTime = (iso?: string) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('no-NO', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (n: number) => {
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(2);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedItemSearch(itemSearch);
  };

  const handleCountSuccess = (
    batchID: string,
    lineCount: number,
    kind: CountFromDiffResultKind,
    showToast = true
  ) => {
    setModalOpen(false);
    setSelectedLocs(new Set());
    if (showToast) {
      alert(
        t(`warehouse.modules.balanceComparison.toast.${kind}`)
          .replace('{batchID}', batchID)
          .replace('{count}', String(lineCount))
      );
    }
    loadData();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Banner */}
      {summary && (
        <SnapshotInfoBanner lastSnapshotAt={summary.lastSnapshotAt} isStale={summary.isStale} />
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
          <Search size={16} className="text-gray-500" />
          <input
            type="text"
            placeholder={t('warehouse.modules.balanceComparison.filters.searchItem')}
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm w-48"
          />
          <button
            type="submit"
            className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm transition-colors"
          >
            {t('common.buttons.search')}
          </button>
        </form>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={onlyOutOfSync}
            onChange={(e) => setOnlyOutOfSync(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded"
          />
          {t('warehouse.modules.balanceComparison.filters.onlyOutOfSync')}
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={hideMovedSinceSnapshot}
            onChange={(e) => setHideMovedSinceSnapshot(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded"
          />
          {t('warehouse.modules.balanceComparison.filters.hideMovedSinceSnapshot', {}, 'Skjul bevegelse etter ERP-oppdatering')}
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={includeEmptyBins}
            onChange={(e) => setIncludeEmptyBins(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded"
          />
          {t('warehouse.modules.balanceComparison.filters.includeEmptyBins')}
        </label>

        <select
          value={onlyCountable === undefined ? 'all' : onlyCountable ? 'countable' : 'blocked'}
          onChange={(e) => {
            const v = e.target.value;
            setOnlyCountable(v === 'all' ? undefined : v === 'countable');
          }}
          className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
        >
          <option value="all">{t('warehouse.modules.balanceComparison.filters.allCountable')}</option>
          <option value="countable">
            {t('warehouse.modules.balanceComparison.filters.onlyCountable')}
          </option>
          <option value="blocked">{t('warehouse.modules.balanceComparison.filters.onlyBlocked')}</option>
        </select>

        <button
          onClick={loadData}
          disabled={loading}
          className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm flex items-center gap-2 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {t('common.buttons.refresh')}
        </button>

        <div className="flex-grow" />

        {totalSelectedOnPage > 0 && (
          <button
            onClick={() => setModalOpen(true)}
            className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-2 transition-colors"
          >
            <ListChecks size={16} />
            {t('warehouse.modules.balanceComparison.actions.createCountList')} ({totalSelectedOnPage})
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 text-sm border border-red-200 dark:border-red-800">
          <AlertTriangle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
            <tr>
              <th className="px-2 py-2 w-8" />{/* expand */}
              <th className="px-3 py-2 w-10">{/* checkbox */}</th>
              <th className="px-3 py-2 text-left font-medium">
                {t('warehouse.modules.balanceComparison.columns.itemNo')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('warehouse.modules.balanceComparison.columns.description')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('warehouse.modules.balanceComparison.columns.wmsQty')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('warehouse.modules.balanceComparison.columns.erpQty')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('warehouse.modules.balanceComparison.columns.erpReserved', {}, 'ERP-reservert')}
              </th>
              <th className="px-3 py-2 text-right font-medium">
                {t('warehouse.modules.balanceComparison.columns.diff')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('warehouse.modules.balanceComparison.columns.lastMovWms')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('warehouse.modules.balanceComparison.columns.lastMovErp')}
              </th>
              <th className="px-3 py-2 text-left font-medium">
                {t('warehouse.modules.balanceComparison.columns.insight')}
              </th>
              <th className="px-3 py-2 text-center font-medium w-24">
                {t('warehouse.modules.balanceComparison.columns.status')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                  <Loader size={20} className="inline-block animate-spin mr-2" />
                  {t('common.status.loading')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="px-3 py-8 text-center text-gray-500 dark:text-gray-400">
                  {t('common.messages.noResults')}
                </td>
              </tr>
            ) : (
              rows.flatMap((row) => {
                const blocked = !row.isCountable;
                const isErpOnly = row.presence === 'ErpOnly';
                const isWmsOnly = row.presence === 'WmsOnly';
                const expanded = expandedRows.has(row.id);
                const selectionState = itemSelectionState(row);
                const selectableCount = selectableLocations(row).length;
                const allLockedByOthers =
                  row.locations.length > 0 && row.locations.every((l) => l.isOnActiveCountList);

                const parentClass = blocked
                  ? 'bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-500'
                  : selectionState === 'all'
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50';

                const parentRow = (
                  <tr key={row.id} className={`${parentClass} transition-colors`}>
                    <td className="px-2 py-2 align-middle">
                      {!isErpOnly && (
                        <button
                          onClick={() => toggleExpand(row)}
                          className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                          aria-label={expanded ? t('common.buttons.hideDetails') : t('common.buttons.showDetails')}
                        >
                          {expanded ? <ChevronDown size={16} /> : <ChevronRightIcon size={16} />}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2 align-middle">
                      <input
                        type="checkbox"
                        checked={selectionState === 'all'}
                        ref={(el) => {
                          if (el) el.indeterminate = selectionState === 'some';
                        }}
                        onChange={() => toggleItem(row)}
                        disabled={blocked || selectableCount === 0}
                        className="w-4 h-4 text-blue-600 rounded disabled:opacity-30"
                        aria-label={`${row.itemNo}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.itemNo}
                      {isErpOnly && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                          title={t('warehouse.modules.balanceComparison.badges.erpOnlyTooltip')}
                        >
                          <Tag size={10} />
                          {t('warehouse.modules.balanceComparison.badges.erpOnly')}
                        </span>
                      )}
                      {isWmsOnly && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
                          title={t('warehouse.modules.balanceComparison.badges.wmsOnlyTooltip')}
                        >
                          <Tag size={10} />
                          {t('warehouse.modules.balanceComparison.badges.wmsOnly')}
                        </span>
                      )}
                      {allLockedByOthers && !blocked && (
                        <span
                          className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                          title={t('warehouse.modules.balanceComparison.badges.allOnCountListTooltip')}
                        >
                          <ClipboardList size={10} />
                          {t('warehouse.modules.balanceComparison.badges.onCountList')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 truncate max-w-xs" title={row.description}>
                      {row.description ?? '-'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatNumber(row.wmsQuantity)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatNumber(row.erpQuantity)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {row.erpReserved == null ? '—' : formatNumber(row.erpReserved)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums font-medium ${
                        row.diff !== 0 ? 'text-red-600 dark:text-red-400' : ''
                      }`}
                    >
                      {row.diff > 0 ? '+' : ''}
                      {formatNumber(row.diff)}
                    </td>
                    <td className="px-3 py-2 text-xs">{formatDate(row.lastMovementWms)}</td>
                    <td className="px-3 py-2 text-xs">{formatDate(row.lastMovementErp)}</td>
                    <td className="px-3 py-2">
                      <MovementInsightBadge insight={row.movementInsight} />
                    </td>
                    <td className="px-3 py-2 text-center">
                      {blocked ? (
                        <span title={row.countBlockReason ?? ''} className="inline-flex">
                          <Lock size={16} className="text-gray-400" />
                        </span>
                      ) : row.inSync ? (
                        <span className="text-green-600 dark:text-green-400">✓</span>
                      ) : (
                        <span className="text-red-600 dark:text-red-400">●</span>
                      )}
                    </td>
                  </tr>
                );

                if (!expanded || isErpOnly) return [parentRow];

                // Child rows for each location
                const childRows = row.locations.map((loc) => {
                  const key = locKey(row.warehouseID, row.itemNo, loc.binCode);
                  const isSelected = selectedLocs.has(key);
                  const childBlocked = blocked || loc.isOnActiveCountList;
                  const childClass = childBlocked
                    ? 'bg-gray-50/50 dark:bg-gray-900/30 text-gray-500 dark:text-gray-500'
                    : isSelected
                      ? 'bg-blue-50/70 dark:bg-blue-900/15'
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30';

                  return (
                    <tr key={`${row.id}__${loc.binCode}`} className={`${childClass} transition-colors text-xs`}>
                      <td className="px-2 py-1.5" />
                      <td className="px-3 py-1.5 align-middle pl-8">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleLocation(row, loc)}
                          disabled={childBlocked}
                          className="w-4 h-4 text-blue-600 rounded disabled:opacity-30"
                          aria-label={`${row.itemNo} ${loc.binCode}`}
                        />
                      </td>
                      <td className="px-3 py-1.5 font-mono pl-6 text-gray-500" colSpan={2}>
                        ↳ <strong>{loc.binCode || t('manager.modules.inventoryCountingDashboard.balanceComparison.noBin', {}, '(ingen)')}</strong>
                        {loc.stcBinCatNo != null && binCategoryTextMap.has(loc.stcBinCatNo) && (
                          <span
                            className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium font-sans ${categoryBadgeClass(loc.stcBinCatNo)}`}
                            title={t('manager.modules.inventoryCountingDashboard.balanceComparison.binCategory', {}, 'Lokasjonskategori')}
                          >
                            {binCategoryTextMap.get(loc.stcBinCatNo)}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {formatNumber(loc.wmsQuantity)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">—</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">—</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-400">—</td>
                      <td className="px-3 py-1.5">{formatDateTime(loc.lastMovementWms)}</td>
                      <td className="px-3 py-1.5 text-gray-400">—</td>
                      <td className="px-3 py-1.5">
                        {loc.isOnActiveCountList && (
                          <span
                            className="inline-flex items-center gap-1 text-purple-700 dark:text-purple-300"
                            title={
                              loc.activeCountListOperatorName
                                ? t('manager.modules.inventoryCountingDashboard.balanceComparison.countedOnBy', { batch: loc.activeCountListBatchID ?? '', operator: loc.activeCountListOperatorName }, `Telles på ${loc.activeCountListBatchID}, av ${loc.activeCountListOperatorName}`)
                                : t('manager.modules.inventoryCountingDashboard.balanceComparison.countedOn', { batch: loc.activeCountListBatchID ?? '' }, `Telles på ${loc.activeCountListBatchID}`)
                            }
                          >
                            <ClipboardList size={12} />
                            {loc.activeCountListOperatorName ?? loc.activeCountListBatchID ?? ''}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        {childBlocked ? (
                          <Lock size={14} className="text-gray-400 inline-block" />
                        ) : isSelected ? (
                          <span className="text-blue-600 dark:text-blue-400">✓</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                });

                return [parentRow, ...childRows];
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
        <div>
          {totalCount > 0 && (
            <span>
              {t('warehouse.modules.balanceComparison.pagination.showing')
                .replace('{from}', String((page - 1) * PAGE_SIZE + 1))
                .replace('{to}', String(Math.min(page * PAGE_SIZE, totalCount)))
                .replace('{total}', String(totalCount))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1 || loading}
            className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages || loading}
            className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Modal */}
      <CreateCountFromDiffModal
        isOpen={modalOpen}
        selectedLines={selectedLines}
        warehouseID={warehouseID}
        operators={profiles.map((p) => ({
          operatorID: p.userID,
          operatorName: p.profileName,
        }))}
        onClose={() => setModalOpen(false)}
        onSuccess={handleCountSuccess}
      />
    </div>
  );
};
